/**
 * Input Variants
 *
 * CVA variants for the Input component.
 * Exported separately to comply with React Fast Refresh requirements.
 *
 * @see Input.tsx for component implementation
 */

import {cva} from 'class-variance-authority';

/**
 * Input style variants using class-variance-authority.
 *
 * Base styles include:
 * - Consistent sizing from design tokens
 * - Focus ring using --ring token
 * - Smooth Apple-quality transitions
 * - Mobile touch targets (min 44px)
 */
export const inputVariants = cva(
  [
    // Base layout & sizing
    "flex w-full rounded-apple-lg border bg-card",
    // Typography
    "text-sm text-foreground",
    // File input styling
    "file:border-0 file:bg-transparent file:text-sm file:font-medium",
    // Placeholder
    "placeholder:text-muted-foreground",
    // Transitions (Apple-quality)
    "transition-all duration-300 ease-apple",
    // Focus ring from design tokens
    "focus-visible:outline-none",
    "focus-visible:ring-2",
    "focus-visible:ring-ring",
    "focus-visible:ring-offset-2",
    "focus-visible:ring-offset-background",
    // Disabled state
    "disabled:cursor-not-allowed",
    "disabled:opacity-50",
    // Mobile optimizations
    "touch-manipulation",
    // Hover effect
    "hover:shadow-apple-sm",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "border-input",
          "shadow-apple-sm",
          "focus-visible:border-primary",
          "focus-visible:shadow-apple-lg",
          "focus-visible:bg-card/100",
        ].join(" "),
        search: [
          "border-input",
          "bg-muted/50",
          "shadow-none",
          "focus-visible:ring-1",
          "focus-visible:ring-primary/50",
          "focus-visible:border-primary",
          "focus-visible:bg-card",
        ].join(" "),
        minimal: [
          "border-transparent",
          "bg-transparent",
          "shadow-none",
          "hover:bg-muted/10",
          "focus-visible:border-primary",
          "focus-visible:bg-muted/30",
        ].join(" "),
        tangi: [
          "border-primary/20",
          "bg-card/80",
          "backdrop-blur-sm",
          "focus-visible:border-primary",
          "focus-visible:ring-primary/40",
        ].join(" "),
      },
      inputSize: {
        sm: "h-9 px-3 text-xs rounded-apple-md min-h-touch sm:min-h-0",
        default: "h-11 px-4 py-2 text-sm min-h-touch sm:min-h-0",
        lg: "h-12 px-5 py-3 text-base rounded-apple-xl min-h-12 sm:min-h-0",
      },
      error: {
        true: [
          "border-destructive",
          "focus-visible:ring-destructive/20",
          "focus-visible:border-destructive",
          "text-destructive",
          "placeholder:text-destructive/60",
        ].join(" "),
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
      error: false,
    },
  }
);
